import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FeatureFlag, PrometheusTab, SystemRoles } from '../../types/generic';
import { fallbackTab, getAllowedTabs, getDefaultTab, prometheusTabs } from '../../components/Tabs';

export interface TabState {
	currentTab: string | null;
	previousTabs: string[];
	possibleTabs: string[];
	tabsToDisplay: string[];
	userRoles: SystemRoles[];
	featureFlags: FeatureFlag[];
	queuedTab?: string;
}

const initialState: TabState = {
	currentTab: 'Upload.Cellebrite',
	previousTabs: [],
	possibleTabs: [],
	tabsToDisplay: [],
	userRoles: [],
	featureFlags: [],
};

const tabSlice = createSlice({
	name: 'tab',
	initialState,
	reducers: {
		setUserRoles: (state, action: PayloadAction<{ roles: SystemRoles[]; flags?: FeatureFlag[] }>) => {
			const newRoles = Array.from(new Set(action.payload.roles));
			if (newRoles !== state.userRoles) {
				const newTabs = getAllowedTabs(newRoles);
				//userRoles = [] is a stand in for first login
				if (state.userRoles.length === 0) {
					return {
						...state,
						currentTab: getDefaultTab(newRoles),
						possibleTabs: newTabs,
						userRoles: newRoles,
						featureFlags: action.payload.flags || [],
					};
				} else {
					return {
						...state,
						possibleTabs: newTabs,
						userRoles: newRoles,
					};
				}
			}
		},
		addUserRoles: (state, action: PayloadAction<SystemRoles[]>) => {
			const newRoles = Array.from(new Set([...state.userRoles, ...action.payload]));
			if (newRoles !== state.userRoles) {
				const newTabs = getAllowedTabs(newRoles);
				return {
					...state,
					possibleTabs: newTabs,
					userRoles: newRoles,
				};
			}
		},
		removeUserRoles: (state, action: PayloadAction<SystemRoles[]>) => {
			const newRoles = Array.from(new Set([...state.userRoles].filter((role) => action.payload.includes(role))));
			if (newRoles !== state.userRoles) {
				const newTabs = getAllowedTabs(newRoles);
				return {
					...state,
					possibleTabs: newTabs,
					userRoles: newRoles,
				};
			}
		},
		switchTab: (state, action: PayloadAction<string>) => {
			if (state.currentTab !== action.payload) {
				return {
					...state,
					previousTabs: [state.currentTab as string, ...state.previousTabs],
					currentTab: action.payload,
				};
			} else {
				return { ...state };
			}
		},
		goBack: (state) => {
			return {
				...state,
				currentTab: state.previousTabs[0],
				previousTabs: state.previousTabs.slice(1),
			};
		},
		queueTab: (state, action: PayloadAction<string>) => {
			return {
				...state,
				queuedTab: action.payload,
			};
		},
		clearQueuedTab: (state) => {
			return {
				...state,
				queuedTab: undefined,
			};
		},
	},
});

const childrenToTabs = (tabs: PrometheusTab[], tabIdx: number, curPath: string, paths: string[]): number[] => {
	const tabPath = curPath ? `${curPath}.${tabs[tabIdx].name}` : tabs[tabIdx].name;
	const curTab = tabs[tabIdx];
	const deletedTabs: number[] = [];

	if (!paths.includes(tabPath)) {
		deletedTabs.push(tabIdx);
	} else {
		if (curTab.children) {
			const childTabs = curTab.children;
			let deletedChildTabs: number[] = [];
			childTabs.forEach((_, idx) => {
				deletedChildTabs = [...deletedChildTabs, ...childrenToTabs(childTabs, idx, tabPath, paths)];
			});

			deletedChildTabs.reverse().forEach((idx) => {
				curTab.children?.splice(idx, 1);
			});
		}
	}

	// remove any parent tabs that don't have children any more
	if (!curTab.component && (!curTab.children || curTab.children.length === 0)) {
		deletedTabs.push(tabIdx);
	}

	return deletedTabs;
};

/**
 * Converts an array of paths (possibleTabs) into a array of tabs, effectively de-serialising them
 * @returns subset of the system tabs only matching those in the paths array
 */
export const getTabs = createSelector([(state) => state.tab.possibleTabs], (paths: string[]): PrometheusTab[] => {
	const tabs: PrometheusTab[] = [...prometheusTabs];

	let deletedTabs: number[] = [];
	tabs.forEach((_, idx) => {
		deletedTabs = [...deletedTabs, ...childrenToTabs(tabs, idx, '', paths)];
	});

	// have to splice in reverse to avoid issues with index shifting when something is removed
	deletedTabs.reverse().forEach((idx) => {
		tabs.splice(idx, 1);
	});

	if (tabs.length === 0) {
		tabs.push(fallbackTab);
	}
	return tabs;
});

export const getTabPaths = createSelector([(state) => state.tab.currentTab], (currentTab: string): string[] => {
	const tabsSplit = currentTab.split('.');
	const paths: string[] = [tabsSplit[0]];

	tabsSplit.splice(1).forEach((tabName) => {
		paths.push(`${paths[paths.length - 1]}.${tabName}`);
	});

	return paths;
});

export const { setUserRoles, addUserRoles, removeUserRoles, switchTab, goBack, queueTab, clearQueuedTab } = tabSlice.actions;
export default tabSlice.reducer;
